/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { TranslateStage } from '../translate-stage';
import { TranslateServerSideModel } from '../../models/translate-server-side-model';
import { SseState, TranslateStep } from '../../types';
import { OriginalInputModel } from '@/domains/translate-bot/models/original-input-model';
import { GetRecommendTranslateKeywordService } from '../get-recommend-translate-keyword-service';
import { ASYNC_THRESHOLD } from '../../constants';
import { GetTranslateSegmentsService } from '../get-translate-segments-service';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { TranslationTaskNameGetter } from '../task-name-manager';
import { TranslateLoadingService } from '../translate-loading-service';
import type { Disposable } from '@patsnap/tahiti-core';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';
import { GetTranslateServerSideStateService } from '../get-translate-task-status-service';

@injectable()
export class TranslateStageResult implements TranslateStage {
    constructor(
        @inject(OriginalInputModel)
        private _originalInputModel: OriginalInputModel,
        @inject(GetRecommendTranslateKeywordService)
        private _getRecommendTranslateKeywordService: GetRecommendTranslateKeywordService,
        @inject(GetTranslateSegmentsService)
        private _getTranslateSegmentsService: GetTranslateSegmentsService,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
        @inject(TranslationTaskNameGetter)
        private _taskNameGetter: TranslationTaskNameGetter,
        @inject(TranslateLoadingService)
        private _translateLoadingService: TranslateLoadingService,
        @inject(RavenService)
        private _ravenService: RavenService,
        @inject(GetTranslateServerSideStateService)
        private _getTranslateServerSideStateService: GetTranslateServerSideStateService,
    ) {

    }

    isPreviousStage(model: TranslateServerSideModel): boolean {
        return model.modelData.currentStep === TranslateStep.StepPreview;
    }

    isCurrentStage(model: TranslateServerSideModel): boolean {
        return model.modelData.currentStep === TranslateStep.StepResult;
    }

    onLeave(): Promise<void> {
        this._ravenService.track(TrackEvents.TRACK_EVENT_LEAVE_TRANSLATION_RESULT, {
            eventType: TRACK_EVENT_TYPE.Translation,
            otherParam: this.taskId,
        });
        return Promise.resolve();
    }

    isNextStage(model: TranslateServerSideModel): boolean {
        return model.modelData.currentStep === TranslateStep.StepInitial
            && !this.isAsyncThreshold;
    }

    async onEnter(model: TranslateServerSideModel): Promise<void> {
        this._ravenService.track(TrackEvents.TRACK_EVENT_ENTER_TRANSLATION_RESULT, {
            eventType: TRACK_EVENT_TYPE.Translation,
            otherParam: this.taskId,
        });
        model.update(states => {
            return {
                ...states,
                currentStep: TranslateStep.StepResult,
            };
        });
        this._getTaskName();
        const disposables: Disposable[] = [];
        try {
            this._translateLoadingService.setLoadingTranslateResult(true);
            this._translateLoadingService.setWaitingFirstResponse(true);
            disposables.push(this._getRecommendTranslateKeyword());
            await this._getTranslateSegments();
        }
        finally {
            disposables.forEach(d => d.dispose());
            this._translateLoadingService.setLoadingTranslateResult(false);
        }
    }


    private get taskId() {
        return this._currentTaskService.taskId;
    }

    /**
     * 获取推荐词
     */
    private _getRecommendTranslateKeyword() {
        const dispose: Disposable[] = [];
        this._ravenService.track(TrackEvents.TRACK_EVENT_GET_RECOMMEND_TRANSLATE_KEYWORD, {
            eventType: TRACK_EVENT_TYPE.Translation,
            otherParam: this.taskId,
        });

        const disposable = this._getTranslateSegmentsService.onStateChange(state => {
            if (state === SseState.Outputting) {
                /* 从服务端拉取翻译状态 */
                this._getTranslateServerSideStateService.pullTranslateServerSideState(this.taskId);
                /* 为了达到，在翻译段落输出时，获取推荐词的效果，这里需要监听翻译段落输出状态，再获取推荐词 */
                this._getRecommendTranslateKeywordService.getRecommendTranslateKeyword(this.taskId)
                    .catch(() => {
                        this._ravenService.track(TrackEvents.TRACK_EVENT_GET_RECOMMEND_TRANSLATE_KEYWORD_FAILED, {
                            eventType: TRACK_EVENT_TYPE.Translation,
                            otherParam: this.taskId,
                        });
                    }).finally(() => {
                        dispose.forEach(d => d.dispose());
                        dispose.length = 0;
                    });

            }
        });

        dispose.push(disposable);

        return disposable;
    }


    /**
     * 获取任务名称
     * @returns 任务名称
     */
    private _getTaskName(): Promise<string> {
        return this._taskNameGetter.getTaskName(this._currentTaskService.taskId);
    }

    /**
     * 获取翻译段落
     */
    private _getTranslateSegments() {
        const taskId = this._currentTaskService.taskId;
        return this._getTranslateSegmentsService.getTranslateSegments(taskId);
    }

    /**
     * 是否达到异步翻译任务阈值
     */
    private get isAsyncThreshold() {
        return this._originalInputModel.modelData.input.length > ASYNC_THRESHOLD;
    }
}
